import { Component, DetailEvent, Entity, MultiPropertySchema, registerComponent, registerSystem, schema, System } from "aframe";

type Attributes = { [name : string] : string | any };
interface TemplateDefinition
{
	type? : string;
	attributes? : Attributes,
	children? : Template[]
}
type Template = TemplateDefinition | Attributes | Template[];

const isTemplateDefinition = (template : Template) =>
{
	if(Array.isArray(template)) return false;
	return ("attributes" in template);  //("type" in template) || ("attributes" in template) || ("children" in template);
}

const applyAttributes = ( el : Entity, attributes : Attributes ) =>
{
	for(let attr in attributes)
	{
		if(!attr) continue; //Children in short templates.
		if(attr.startsWith("//")) continue; //Comment.
		
		const attrValue = attributes[attr];
		if( attrValue && (attrValue.constructor === Object) )
		{
			const existingComponent = el.components[ attr ];
			const preserveExistingProperties = existingComponent && ("" in existingComponent.schema);
			
			
			const safeValue : any = {};
			for(let prop in attrValue)
			{
				const propValue = attrValue[prop];
				if(preserveExistingProperties && (prop in existingComponent.data))
					continue;	//Don't overwrite.

				if(typeof propValue == "string")
				{
					//Protect string inside String to prevent AFrame from parsing them
					safeValue[prop] = new String(propValue);
				}
				else
					safeValue[prop] = propValue;
			}

			el.setAttribute(attr, safeValue);
			continue;
		}
		
		if(attr == "class") //Special case : append existing classes
		{
			el.className = el.className + ' ' + attrValue;
			continue;
		}

		el.setAttribute(attr, attrValue);
	}
};

const revertAttributes = ( el : Entity, attributes : Attributes ) =>
{
	const keys = Object.keys(attributes);
	for(let index = keys.length; --index >= 0; )
	{
		const attr = keys[index];
		if(!attr) continue; //Children in short templates.
		if(attr.startsWith("//")) continue; //Comment.

		const attrValue = attributes[attr];
		if( attrValue && (attrValue.constructor === Object) )
		{
			const component = el.components[ attr ] as Component &
			{
				attrValue : { [property:string] : any },
				updateProperties : (attrValue : any)  => void
			};

			const schema = component.schema;
			

			if( (typeof(schema.type) !== 'string') && !('default' in schema) ) //multi-prop schema
			{
				const schemaProps = schema as MultiPropertySchema<any>;
				const defProp = schemaProps[''];

				//Remove all properties at the same time, then update the component once unless it has no more properties :

				for(let prop in attrValue)
				{
					delete component.attrValue[prop];		
					if(prop in schemaProps)
						component.data[prop] = schemaProps[prop].default;
					else if(defProp)
						delete component.data[prop];
				}

				for(let anyRemainingProp in component.attrValue)
				{
					component.updateProperties(component.attrValue);
					return;
				}	
			}
		}

		if(attr == "class") //attrValue was appended to className, just remove :
		{
			const classes = String(attrValue);
			const ix = el.className.indexOf(classes);
			if(ix >= 0)
				el.className = el.className.slice(0, ix) + el.className.slice(ix + attrValue.length);
			return;
		}
		

		el.removeAttribute(attr);
	}
};
const applyChildren = ( promises : Promise<any>[], el : Entity, templates : Template[], children? : Entity[] ) =>
{
	for(let childT of templates)
	{
		const child = el.ownerDocument.createElement( (childT as TemplateDefinition).type ?? "a-entity") as Entity;
		promises.push( new Promise((resolve, reject) => child.addEventListener("loaded", resolve)) );

		const attachedListener = (event : DetailEvent<{ el : Entity }>) =>
		{
			if(event.detail.el === child)
			{
				event.stopPropagation();
				event.preventDefault();
			}
		};

		// Prevent child-attached from being dispatched before the child is set up
		el.addEventListener("child-attached", attachedListener as EventListener, { capture : true } );

		// Child needs to be added to the document first, so that selectors resolve correctly :
		el.appendChild(child);

		el.removeEventListener("child-attached", attachedListener as EventListener, { capture : true } );

		applyTemplate(promises, child, childT);

		// Dispatch child-attached now that attributes are applied, when child is loaded.
		if(child.hasLoaded)
			el.emit("child-attached", {el : child});
		else 
			child.addEventListener("loaded", () => el.emit("child-attached", {el : child}), { once : true });

		children && children.push(child);
	}
}

const applyTemplate = ( promises : Promise<any>[], el : Entity, template : Template, children? : Entity[] ) =>
{
	if( Array.isArray(template) )
	{
		return applyChildren(promises, el, template, children);
	}
	else if( "" in template ) //Hack-ish shortcut : children are stored in the obviously invalid empty key
	{
		const childrenTemplates = template[""] as Template[];
		
		// Apply children before attributes to make child selectors work :
		childrenTemplates && applyChildren(promises, el, childrenTemplates, children);

		applyAttributes(el, template);
	}
	else if( isTemplateDefinition(template) )
	{
		// Apply children before attributes to make child selectors work :
		template.children && applyChildren(promises, el, template.children, children);

		template.attributes && applyAttributes(el, template.attributes);
	}
	else
		applyAttributes( el, template as Attributes );
};

const revertTemplate = ( el : Entity, template : Template, children : Entity[] ) =>
{
	// Attributes were applied after children.
	// Revert them before children.

	if( !Array.isArray(template) ) //Children only
	{
		if( "" in template )
			revertAttributes(el, template as Attributes);
		else if(isTemplateDefinition(template))
			template.attributes && revertAttributes(el, template.attributes);
		else
			revertAttributes(el, template as Attributes);
	}

	if(children)
		for(let child of children)
			el.removeChild(child);
}


type Defines = { [name:string] : any }

const merge = (template : any, define : any) =>
{
	for(let key in define)
	{
		const currentValue = template[key], defineValue = define[key];
		if(currentValue === undefined)
		{
			template[key] = define[key];
			continue;
		}
		
		const type = typeof currentValue;
		if(type !== typeof defineValue)
		{
			console.warn("Incompatible types : " + key);
			continue;
		}
		if(type !== "object")
		{
			//Keep current value.
			continue;
		}

		if(Array.isArray(currentValue) && Array.isArray(defineValue))
		{
			if((key == "") || (key == "children")) //Merge children
				template.unshift(...define);
		}
		else if(currentValue && defineValue)
			merge(currentValue, defineValue);
	}
}

const includeDefines = (template : any, defines : Defines, ...includes : string[] ) =>
{
	for(let defName of includes)
	{
		const def = defines[defName];
		if(def)
			merge(template, def);
		else
			console.warn("Missing #define : " + defName);
	}
}

const preprocessTemplate = (template : any, defines : Defines) =>
{
	if(Array.isArray(template))
	{
		for(let child of template)
			preprocessTemplate(child, defines);
	}
	else if(template && (typeof(template) === "object"))
	{
		const define = template["#define"], include = template["#include"];
		delete template["#define"];
		delete template["#include"];
		if(define)
		{
			const newDefines : Defines = {};
			for(let key in defines)
				newDefines[key] = defines[key];
			for(let key in define)
				newDefines[key] = define[key];
			defines = newDefines;
		}
	
		if(Array.isArray(include)) //Include : array of defines
			includeDefines(template, defines, ...include);
		else if(typeof include === "string") //
			includeDefines(template, defines, include);

		for(let key in template)
			preprocessTemplate( template[key], defines );
	}
};

export interface TemplateData
{
	template : Template;
	headers : { [key : string] : string };
}

export interface TemplateSystem extends System
{
	data : {
		endpoint : string;
		authentication : string;
		queryString : string;
		preload : string[];
	}
	cache : { [ name : string ] : Promise<TemplateData> };
	get(template : string) : Promise<TemplateData>;
};

registerSystem("template", {
	schema : {
		endpoint : { type : "string", default : "/Templates/" },
		authentication : { type : "string", default : "authenticate-request" },
		queryString : { type: "string", default : "" }, //Additional query parameters passed in each request
		preload : { type : "array" }
	},

	init(this : TemplateSystem)
	{
		this.cache = {};
		for(let t of (this.data.preload || []))
			this.get(t);
	},

	get(this : TemplateSystem, template : string)
	{
		const cache = this.cache;
		const cached = cache[template];
		if(cached)
			return cached;
		
		return cache[template] = new Promise<TemplateData>( async (got, failed) =>
		{
			const baseUrl = new URL(this.data.endpoint, this.el.ownerDocument.location.href);

			const templateUrl = new URL(template, baseUrl);
			
			const qs = this.data.queryString;
			if(qs && /^http(s)?:$/.test(templateUrl.protocol))
				templateUrl.search = (templateUrl.search ? (templateUrl.search + "&") : "?") +
					((qs.startsWith("?") || qs.startsWith("&")) ? qs.slice(1) : qs);

			const request = { headers : {} };
			if( this.data.authentication )
			{
				this.el.emit(this.data.authentication, request);

				if( Object.keys(request.headers).length == 0 ) //No header added <=> not authenticated yet!
				{
					const authPromise = new Promise( (resolve, reject) =>
					{
						const doc = this.el.ownerDocument;
						const listener = (event : Event) =>
						{
							doc.removeEventListener("authenticated", listener);
							doc.removeEventListener("authentication-unavailable", listener);
							((event.type == "authenticated") ? resolve : reject)(event);
						};
						doc.addEventListener("authenticated", listener);
						doc.addEventListener("authentication-unavailable", listener);
					});

					await authPromise;

					this.el.emit(this.data.authentication, request);
				}
			}
			
			const response = await fetch( templateUrl, request );

			if(response.status >= 400)
			{
				console.warn("Failed to load template : " + response.status + " " + response.statusText);
				failed(response);
			}

			const def = (await response.json()) as Template;
			preprocessTemplate(def, {});

			const data : TemplateData = { template : def, headers : {} };
			response.headers.forEach( (val, key) => data.headers[key] = decodeURIComponent(val.replace('+', '%20')) );
			return got(data);
		} );
	}
});



export interface TemplateComponent extends Component
{
	data : string;
	templateLoaded() : boolean;
}


interface TemplateComponentInternal extends Component
{
	data : string;
	system : TemplateSystem;
	template? : Template; //Stores current definition
	headers? : { [key:string] : string };
	children : Entity[]; //Stores child entities created by current definitino
}

export type TemplateEvent = DetailEvent< { component : TemplateComponent, template : string } >;

registerComponent("template", {
	schema : { type : "string" },
	multiple : true,
	init(this : TemplateComponentInternal)
	{
		this.children = [];		
	},
	update(this : TemplateComponentInternal, oldData)
	{
		if(this.data == oldData)
			return; //Should not happen

		const el = this.el;
		if(this.template)
			revertTemplate(el, this.template, this.children);
		delete this.template;
		delete this.headers;
		this.children = [];

		const templateUrl = this.data;
		if(!templateUrl)
			return;

		const eventDetail = {
			component : this,
			template : templateUrl,
		};
		el.emit("template-loading", eventDetail, false);
		this.system.get(templateUrl).then( (templateData) => {
			const promises : Promise<any>[] = [];
			applyTemplate(promises, el, templateData.template, this.children);
			Promise.all(promises).then( () =>
			{
				this.template = templateData.template;
				this.headers = templateData.headers;
				el.emit( "template-loaded", eventDetail, false);
			} );
		} );
	},
	remove(this : TemplateComponentInternal)
	{
		if(!this.el.isConnected)
			return; //Entity removed from DOM : don't try to revert, it's being destroyed.

		if(this.template)
			revertTemplate(this.el, this.template, this.children);
		delete this.template;
		delete this.headers;
	},
	templateLoaded(this : TemplateComponentInternal)
	{
		return Boolean(this.template);
	}
});



interface TemplateInstanceComponent extends Component
{
	data : string;
	instance? : Entity;
}
registerComponent("template-instance", {
	schema : { type : "string" },
	multiple : true,
	update(this : TemplateInstanceComponent)
	{
		if(this.instance)
		{
			this.instance.remove();
			delete this.instance;
		}
		if(this.data)
		{
			this.instance = this.el.ownerDocument.createElement("a-entity") as Entity;
			this.instance.setAttribute("template", this.data);
			this.el.append(this.instance);
		}
	},
	remove(this : TemplateInstanceComponent)
	{
		if(this.instance)
		{
			this.instance.remove();
			delete this.instance;
		}
	}
});

